/**
 * OperacionDiaPage — Vista operativa "Lanchas hoy".
 *
 * Cada fila es una lancha. Cada bloque dentro de la fila representa una
 * salida (pasadía) que esa lancha sirve. El operador ve de un vistazo:
 *
 *   - Qué lanchas operan hoy y cuáles están libres
 *   - Qué pasadías lleva cada lancha (compartidos o dedicados)
 *   - Capacidad agregada por lancha y % ocupado real
 *   - Conflictos visuales si una lancha tiene salidas con horarios distintos
 *
 * Lee de GET /turismo/salidas con el modelo de capacidad compartida ya
 * resuelto en backend (incluye `lancha_compartida`, `comparte_con`,
 * `asientos_disponibles_efectivos`).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, IconButton, CircularProgress, Tooltip, Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import {
  API_BASE, getAuthHeaders, getEmpresaId, formatHora, todayLocal,
} from './shared/format';

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Paleta estable por destino — la misma de CalendarioPage.
const PALETA = [
  { fg: '#0d9488', bg: '#ccfbf1' }, { fg: '#0ea5e9', bg: '#e0f2fe' },
  { fg: '#8b5cf6', bg: '#ede9fe' }, { fg: '#f97316', bg: '#ffedd5' },
  { fg: '#ec4899', bg: '#fce7f3' }, { fg: '#84cc16', bg: '#ecfccb' },
];
function colorPorDestino(nombre) {
  if (!nombre) return { fg: '#64748b', bg: '#f1f5f9' };
  let h = 0;
  for (let i = 0; i < nombre.length; i++) h = ((h << 5) - h + nombre.charCodeAt(i)) | 0;
  return PALETA[Math.abs(h) % PALETA.length];
}


export default function OperacionDiaPage() {
  const empresaId = getEmpresaId();
  const [fecha, setFecha] = useState(todayLocal());
  const [salidas, setSalidas] = useState([]);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}&fecha_desde=${fecha}&fecha_hasta=${fecha}`,
          { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }),
      ]);
      const sData = await sRes.json();
      const eData = await eRes.json();
      setSalidas(Array.isArray(sData) ? sData : (sData.salidas || []));
      setEmbarcaciones(Array.isArray(eData) ? eData : (eData.embarcaciones || []));
    } catch {
      setSalidas([]);
      setEmbarcaciones([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar salidas por embarcación. Como una lancha puede ser compartida
  // entre varias salidas, agrupamos por `embarcacion_id` desde las
  // `embarcaciones_asignadas` de cada salida.
  const filasPorLancha = useMemo(() => {
    const map = new Map();
    for (const s of salidas) {
      const asignadas = s.embarcaciones_asignadas || [];
      if (asignadas.length === 0) continue;
      for (const ea of asignadas) {
        const eid = ea.embarcacion_id;
        if (!map.has(eid)) {
          map.set(eid, {
            embarcacion_id: eid,
            embarcacion_nombre: ea.embarcacion_nombre,
            capacidad: ea.capacidad_asignada || 0,
            salidas: [],
          });
        }
        map.get(eid).salidas.push(s);
      }
    }
    // Lanchas SIN asignar (en flota pero no operando hoy) — útil para que el
    // coordinador vea las disponibles antes de asignar.
    const lanchasAsignadasIds = new Set(map.keys());
    for (const e of embarcaciones) {
      if (!lanchasAsignadasIds.has(e.id)) {
        map.set(e.id, {
          embarcacion_id: e.id,
          embarcacion_nombre: e.nombre,
          capacidad: e.capacidad_total || 0,
          salidas: [],
        });
      }
    }
    // Ordenar: las que operan primero (más salidas), luego las libres
    return [...map.values()].sort((a, b) => {
      if (a.salidas.length > 0 && b.salidas.length === 0) return -1;
      if (a.salidas.length === 0 && b.salidas.length > 0) return 1;
      return b.capacidad - a.capacidad;
    });
  }, [salidas, embarcaciones]);

  const totales = useMemo(() => {
    const lanchasOperando = filasPorLancha.filter(f => f.salidas.length > 0).length;
    const lanchasLibres = filasPorLancha.length - lanchasOperando;
    const capacidadOperando = filasPorLancha
      .filter(f => f.salidas.length > 0)
      .reduce((acc, f) => acc + (f.capacidad || 0), 0);
    let paxTotal = 0;
    for (const s of salidas) paxTotal += (s.asientos_reservados || 0);
    return { lanchasOperando, lanchasLibres, capacidadOperando, paxTotal };
  }, [filasPorLancha, salidas]);

  const cambiarFecha = (delta) => {
    const d = new Date(fecha + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setFecha(localDateStr(d));
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            Operación del día
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Lanchas operando hoy con sus pasadías a bordo. Capacidad compartida resuelta.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Hoy">
          <IconButton size="small" onClick={() => setFecha(todayLocal())}
            sx={{ bgcolor: '#0d9488', color: '#fff', '&:hover': { bgcolor: '#0f766e' } }}>
            <CalendarTodayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => cambiarFecha(-1)} sx={{ bgcolor: '#f1f5f9' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontSize: 14, fontWeight: 700, minWidth: 160, textAlign: 'center', color: '#0d9488' }}>
          {new Date(fecha + 'T12:00:00').toLocaleDateString('es-CO', {
            weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
          })}
        </Typography>
        <IconButton size="small" onClick={() => cambiarFecha(1)} sx={{ bgcolor: '#f1f5f9' }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* KPIs del día */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <Kpi label="Lanchas operando" value={totales.lanchasOperando} color="#0d9488" />
        <Kpi label="Lanchas libres" value={totales.lanchasLibres} color="#64748b" />
        <Kpi label="Capacidad total" value={totales.capacidadOperando} color="#8b5cf6" />
        <Kpi label="Pax confirmados" value={totales.paxTotal} color="#0ea5e9"
          sub={totales.capacidadOperando > 0
            ? `${Math.round(totales.paxTotal / totales.capacidadOperando * 100)}% ocupación`
            : ''} />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      ) : filasPorLancha.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
            No hay lanchas en flota.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {filasPorLancha.map((f, idx) => (
            <FilaLancha key={f.embarcacion_id} fila={f}
              esUltima={idx === filasPorLancha.length - 1} />
          ))}
        </Paper>
      )}
    </Box>
  );
}


function Kpi({ label, value, color, sub }) {
  return (
    <Paper sx={{
      p: 1.25, borderRadius: 2,
      borderLeft: `4px solid ${color}`,
      background: 'rgba(255,255,255,0.85)',
    }}>
      <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 10, color: '#64748b' }}>{sub}</Typography>}
    </Paper>
  );
}


function FilaLancha({ fila, esUltima }) {
  const opera = fila.salidas.length > 0;
  const paxAbordo = fila.salidas.reduce((acc, s) => acc + (s.asientos_reservados || 0), 0);
  const cap = fila.capacidad || 0;
  const pctOcup = cap > 0 ? (paxAbordo / cap) * 100 : 0;
  const colorOcup = pctOcup >= 90 ? '#dc2626' : pctOcup >= 60 ? '#f59e0b' : '#16a34a';

  // Si la lancha sirve varias salidas, verificar que todas compartan horario
  // (es el viaje físico único del día). Si difieren, es un conflicto operativo.
  const horariosDistintos = fila.salidas.length > 1 && fila.salidas.some(
    s => s.hora_salida !== fila.salidas[0].hora_salida
         || s.hora_regreso !== fila.salidas[0].hora_regreso
  );

  return (
    <Box sx={{
      p: 1.25,
      borderBottom: esUltima ? 'none' : '1px solid #f1f5f9',
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      bgcolor: opera ? '#fff' : '#fafafa',
      opacity: opera ? 1 : 0.65,
    }}>
      {/* Columna izquierda: nombre + capacidad */}
      <Box sx={{ minWidth: 160, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DirectionsBoatIcon sx={{ color: opera ? '#0d9488' : '#94a3b8', fontSize: 24 }} />
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
            {fila.embarcacion_nombre}
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
            Capacidad {fila.capacidad}
          </Typography>
        </Box>
      </Box>

      {/* Columna central: bloques de salidas (uno por pasadía a bordo) */}
      <Box sx={{ flex: 1, minWidth: 280 }}>
        {!opera ? (
          <Chip size="small" label="Libre hoy"
            sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: 10, height: 20 }} />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {fila.salidas.map(s => {
              const col = colorPorDestino(s.destino_nombre);
              const pax = s.asientos_reservados || 0;
              return (
                <Tooltip key={s.id}
                  title={`${s.paquete_nombre} · ${formatHora(s.hora_salida)} - ${formatHora(s.hora_regreso)} · ${pax} pax`}>
                  <Box sx={{
                    px: 1, py: 0.5, borderRadius: 1,
                    bgcolor: col.bg, color: col.fg,
                    borderLeft: `3px solid ${col.fg}`,
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                  }}>
                    <span>{s.destino_nombre || s.paquete_nombre}</span>
                    <Chip size="small" label={`${pax} pax`}
                      sx={{ bgcolor: col.fg, color: '#fff', fontSize: 9, height: 16, fontWeight: 700 }} />
                  </Box>
                </Tooltip>
              );
            })}
            {fila.salidas.length > 1 && !horariosDistintos && (
              <Chip size="small" label="Viaje compartido"
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 9, height: 20, fontWeight: 700 }} />
            )}
            {horariosDistintos && (
              <Chip size="small" label="⚠ Horarios distintos"
                sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: 9, height: 20, fontWeight: 700 }} />
            )}
          </Box>
        )}
      </Box>

      {/* Columna derecha: ocupación */}
      {opera && (
        <Box sx={{ minWidth: 130, textAlign: 'right' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: colorOcup, lineHeight: 1 }}>
            {paxAbordo}/{cap}
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
            {Math.round(pctOcup)}% · {cap - paxAbordo} libres
          </Typography>
          <Box sx={{ mt: 0.5, height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(pctOcup, 100)}%`, height: '100%', bgcolor: colorOcup }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
